// The audio manifest: what `scripts/deploy/audio-publish.py` writes beside the
// transcoded media under `/audio/`. The client never scans a directory —
// this one document is the whole catalogue of playlists and SFX themes.
//
// The manifest is admin-authored output from a host the client does not
// control, and a dev server answers an unpublished path with `index.html`,
// so the parse is defensive in the shape of `parseSettings`: a null return
// means "no audio here", and one malformed entry is dropped rather than
// taking the whole document with it.

import { isObject, str } from '../net/json';

/** One playable track. `url` is same-origin and already carries `?v=`. */
export interface MusicTrack {
  title: string;
  url: string;
  /** Seconds, as ffprobe measured it. 0 where the probe found nothing. */
  duration: number;
}

/** One directory of tracks. `id` is the directory name. */
export interface MusicPlaylist {
  id: string;
  title: string;
  tracks: MusicTrack[];
  /** The tier mark (`themes.md` §4.2): the entitlement key a player needs
   *  to pick this playlist. Absent means free. */
  key?: string;
}

/** What one id overrides of the theme's voice timing. A missing key keeps
 *  the theme default. */
export interface VoiceTiming {
  /** True starts the voice line after the effect, not over it. */
  after?: boolean;
  /** Milliseconds, added on top of the start the `after` rule picked. */
  delay?: number;
}

/** The spoken half of a theme: the same trigger ids, said out loud. The
 *  publish run writes one block per theme, and an older run wrote none. */
export interface SfxVoice {
  /** The theme default for `after`: does a voice line wait for the effect? */
  after: boolean;
  /** The theme default delay in milliseconds, never below 0. */
  delay: number;
  /** Trigger id → the voice-line variant urls for it. An id may carry a
   *  voice line and no effect, or an effect and no voice line. */
  map: Record<string, string[]>;
  /** The ids that time their voice line their own way. */
  timing: Record<string, VoiceTiming>;
  /** Language tag → trigger id → the voice-line urls in that language
   *  (`audio.md` §5.4, R1145). `map` is the source language, English. The
   *  publish run writes the key only for a theme that holds a line in a
   *  second language, so it is absent on most themes and on every older
   *  manifest; the lines in it use `after`, `delay` and `timing` above. */
  locales?: Record<string, Record<string, string[]>>;
}

/** One directory of SFX. `map` is trigger id → the variant urls for it, and
 *  `voice` the spoken lines that play with them. */
export interface SfxTheme {
  id: string;
  title: string;
  map: Record<string, string[]>;
  voice: SfxVoice;
  /** The tier mark, as on `MusicPlaylist`. Absent means free. */
  key?: string;
}

export interface AudioManifest {
  music: {
    /** The playlist id a player with no stored choice gets. May be ''. */
    default: string;
    /** The playlist the menu plays (`audio.md` §3.6). '' degrades to the
     *  default playlist, like every other id in this document. */
    menu: string;
    /** The playlist an arcade match plays (`audio.md` §3.7). '' degrades
     *  to the **menu** playlist and not to the default one: the arcade
     *  page plays the menu playlist in its lobby, so a match with no set
     *  of its own must keep the track the player is already hearing. */
    arcade: string;
    playlists: MusicPlaylist[];
  };
  sfx: {
    /** The theme id a player with no stored choice gets. May be ''. */
    default: string;
    themes: SfxTheme[];
  };
}

/** A track or clip URL without its `?v=` cache stamp: the name two publish
 *  runs of one file share. The director and the theme workshop match a
 *  stored URL to the manifest through it. */
export function bareUrl(url: string): string {
  const i = url.indexOf('?');
  return i < 0 ? url : url.slice(0, i);
}

/** The entries of a list that `parse` reads, in their order; a malformed
 *  entry is dropped, and a value that is no list reads as none. */
function parseList<T>(v: unknown, parse: (entry: unknown) => T | null): T[] {
  const out: T[] = [];
  if (!Array.isArray(v)) return out;
  for (const entry of v) {
    const parsed = parse(entry);
    if (parsed) out.push(parsed);
  }
  return out;
}

/** A voice delay in milliseconds: a finite figure of 0 or more, or
 *  undefined for anything else. */
function delayOf(v: unknown): number | undefined {
  return typeof v === 'number' && isFinite(v) && v >= 0 ? v : undefined;
}

/** One track survives only with a title and a url. A missing or non-finite
 *  duration becomes 0 — the element reports the real one once it loads. */
function parseTrack(v: unknown): MusicTrack | null {
  if (!isObject(v)) return null;
  const title = str(v.title);
  const url = str(v.url);
  if (!title || !url) return null;
  const d = v.duration;
  return { title, url, duration: typeof d === 'number' && isFinite(d) && d > 0 ? d : 0 };
}

/** A playlist with no id is dropped; a playlist whose tracks were all
 *  malformed is kept empty, so the UI can still say the name and stay quiet. */
function parsePlaylist(v: unknown): MusicPlaylist | null {
  if (!isObject(v)) return null;
  const id = str(v.id);
  if (!id) return null;
  return { id, title: str(v.title) || id, tracks: parseList(v.tracks, parseTrack), ...keyOf(v) };
}

/** The optional tier mark of a playlist or a theme. A missing, empty or
 *  non-string `key` leaves the entry free, so a document an older publish
 *  run wrote parses to the same free entries it always did. */
function keyOf(v: Record<string, unknown>): { key?: string } {
  const key = str(v.key);
  return key ? { key } : {};
}

/** A trigger id → variant urls table. An entry with no url at all is
 *  dropped, so a caller never has to test for an empty variant list. The
 *  look resolver reads a custom SFX body through it as well (themes.md §3.3). */
export function parseClipMap(v: unknown): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  if (!isObject(v)) return map;
  for (const [trigger, urls] of Object.entries(v)) {
    if (!trigger || !Array.isArray(urls)) continue;
    const kept = urls.filter((u): u is string => typeof u === 'string' && u !== '');
    if (kept.length > 0) map[trigger] = kept;
  }
  return map;
}

/** The voice block of one theme. A missing or malformed block reads as "this
 *  theme says nothing", which is what every publish run before the block
 *  wrote. A per-id entry survives only where at least one key is usable. */
export function parseVoice(v: unknown): SfxVoice {
  const out: SfxVoice = { after: false, delay: 0, map: {}, timing: {} };
  if (!isObject(v)) return out;
  if (typeof v.after === 'boolean') out.after = v.after;
  out.delay = delayOf(v.delay) ?? 0;
  out.map = parseClipMap(v.map);
  if (isObject(v.timing)) {
    for (const [trigger, t] of Object.entries(v.timing)) {
      if (!trigger || !isObject(t)) continue;
      const entry: VoiceTiming = {};
      if (typeof t.after === 'boolean') entry.after = t.after;
      const delay = delayOf(t.delay);
      if (delay !== undefined) entry.delay = delay;
      if (entry.after !== undefined || entry.delay !== undefined) out.timing[trigger] = entry;
    }
  }
  // `locales.<tag>.map`, the shape of `map`. A tag with no usable line is
  // dropped, and no usable tag leaves the key out, so an English-only
  // theme parses to what it always did.
  if (isObject(v.locales)) {
    const locales: Record<string, Record<string, string[]>> = {};
    for (const [tag, entry] of Object.entries(v.locales)) {
      if (!tag || !isObject(entry)) continue;
      const map = parseClipMap(entry.map);
      if (Object.keys(map).length > 0) locales[tag] = map;
    }
    if (Object.keys(locales).length > 0) out.locales = locales;
  }
  return out;
}

function parseTheme(v: unknown): SfxTheme | null {
  if (!isObject(v)) return null;
  const id = str(v.id);
  if (!id) return null;
  return {
    id, title: str(v.title) || id, map: parseClipMap(v.map), voice: parseVoice(v.voice), ...keyOf(v),
  };
}

/**
 * Parses `/audio/manifest.json`. Returns null where the document is not a
 * manifest at all — garbage, an array, a dev server's HTML, a body with
 * neither section. An empty `music` or `sfx` section is valid: an admin who
 * published only SFX gets SFX, and the UI hides what has nothing in it.
 */
export function parseManifest(json: unknown): AudioManifest | null {
  if (!isObject(json)) return null;
  if (!isObject(json.music) && !isObject(json.sfx)) return null;

  const music = isObject(json.music) ? json.music : {};
  const playlists = parseList(music.playlists, parsePlaylist);
  const sfx = isObject(json.sfx) ? json.sfx : {};
  const themes = parseList(sfx.themes, parseTheme);

  return {
    music: {
      default: str(music.default),
      menu: str(music.menu),
      // An older publish run wrote no `arcade` key at all. '' is what a
      // missing key means, and the slot degrades from there.
      arcade: str(music.arcade),
      playlists,
    },
    sfx: { default: str(sfx.default), themes },
  };
}

/** True where an entry is free, or `held` holds its tier mark. No `held`
 *  means no gate: the manifest's own slots (menu, arcade) are not picks. */
function open(e: { key?: string }, held?: ReadonlySet<string>): boolean {
  return !held || !e.key || held.has(e.key);
}

/** The entry `id` names, else the manifest's default `def`, else the first
 *  one — each only where the held keys open it. */
function pick<T extends { id: string; key?: string }>(
  list: readonly T[], id: string, def: string, held?: ReadonlySet<string>,
): T | null {
  return list.find((e) => e.id === id && open(e, held))
    ?? list.find((e) => e.id === def && open(e, held))
    ?? list.find((e) => open(e, held))
    ?? null;
}

/** The playlist an id selects. '' or an unknown id degrades to the
 *  manifest default, and that to the first published playlist. With
 *  `held`, a keyed playlist the set does not hold is passed over, the same
 *  way (themes.md §4.2). */
export function pickPlaylist(
  m: AudioManifest, id: string, held?: ReadonlySet<string>,
): MusicPlaylist | null {
  return pick(m.music.playlists, id, m.music.default, held);
}

/** The theme an id selects, degrading the same way as `pickPlaylist`. */
export function pickTheme(m: AudioManifest, id: string, held?: ReadonlySet<string>): SfxTheme | null {
  return pick(m.sfx.themes, id, m.sfx.default, held);
}
